import React from 'react';
import { Shield } from 'lucide-react';
import Header from './components/Header5';
import Footer from './components/Footer5';

const PrivacyPolicy = () => {
    const sections = [
        {
            title: '1. Information We Collect',
            content: 'We collect information that you provide directly to us, including your name, email address, postal address, phone number, and payment information when you make a purchase, create an account, subscribe to our newsletter, or contact us. We also automatically collect certain information about your device when you use our website, including your IP address, browser type, operating system, and browsing behavior.'
        },
        {
            title: '2. How We Use Your Information',
            content: 'We use the information we collect to process your orders, communicate with you about your purchases, send you marketing communications (with your consent), improve our website and services, prevent fraud and enhance security, and comply with legal obligations. We may also use your information to personalize your shopping experience and provide customer support.'
        },
        {
            title: '3. Information Sharing',
            content: 'We do not sell, trade, or rent your personal information to third parties. We may share your information with service providers who assist us in operating our website, conducting our business, or servicing you (such as payment processors, shipping carriers, and email service providers). These third parties are obligated to keep your information confidential and use it only for the purposes we specify.'
        },
        {
            title: '4. Cookies and Tracking Technologies',
            content: 'We use cookies and similar tracking technologies to track activity on our website and hold certain information. Cookies are files with small amounts of data that are sent to your browser from a website and stored on your device. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.'
        },
        {
            title: '5. Data Security',
            content: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. We use secure socket layer (SSL) encryption technology to protect sensitive information during transmission. However, no method of transmission over the Internet or method of electronic storage is 100% secure, and we cannot guarantee absolute security.'
        },
        {
            title: '6. Data Retention',
            content: 'We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your personal information, we will securely delete or anonymize it.'
        },
        {
            title: '7. Your Rights',
            content: 'Depending on your location, you may have certain rights regarding your personal information, including the right to access, correct, or delete your personal data, the right to restrict or object to our processing of your data, the right to data portability, and the right to withdraw consent. To exercise these rights, please contact us using the information provided below.'
        },
        {
            title: '8. Third-Party Links',
            content: 'Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to read the privacy policies of any third-party sites you visit.'
        },
        {
            title: '9. Children\'s Privacy',
            content: 'Our website is not intended for children under the age of 13, and we do not knowingly collect personal information from children under 13. If we learn that we have collected personal information from a child under 13, we will take steps to delete that information as soon as possible.'
        },
        {
            title: '10. International Data Transfers',
            content: 'Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. If you are located outside our operating country and choose to provide information to us, please note that we transfer the data to our country and process it there.'
        },
        {
            title: '11. Marketing Communications',
            content: 'With your consent, we may send you marketing emails about our products, special offers, and other information we think you may find interesting. You can opt out of receiving marketing communications at any time by clicking the unsubscribe link in any marketing email or by contacting us directly.'
        },
        {
            title: '12. Changes to This Policy',
            content: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.'
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Header />

            {/* Hero Section */}
            <section className="bg-gray-50 py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Shield className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                        Privacy Policy
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
                    </p>
                    <p className="text-sm text-gray-500 mt-4">
                        Last Updated: January 2024
                    </p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <div className="space-y-8 lg:space-y-12">
                            {sections.map((section, index) => (
                                <div key={index} className="border-b border-gray-200 pb-8 lg:pb-12 last:border-0">
                                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
                                        {section.title}
                                    </h2>
                                    <p className="text-gray-600 leading-relaxed text-base lg:text-lg">
                                        {section.content}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Contact Section */}
                        <div className="mt-12 lg:mt-16 p-6 lg:p-8 bg-gray-50 rounded-lg">
                            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                                Questions About Your Privacy?
                            </h3>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                                If you have any questions about this Privacy Policy or our data practices, please contact us at:
                            </p>
                            <p className="text-gray-900 font-medium">
                                Email: privacy@studio.com
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
